import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Edit, Trash2, Eye, EyeOff, Image, Video, Youtube, Loader2, Save, X, ImagePlus, Sparkles, Bot, Calendar, Wand2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { safeFetch } from "../utils/safeFetch";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const curriculumOptions = [
  { id: "1", name: "자존감학과" },
  { id: "2", name: "소통기술학과" },
  { id: "3", name: "여성학과" },
  { id: "4", name: "남성학과" },
  { id: "5", name: "연애학과" },
  { id: "6", name: "매력학과" },
  { id: "7", name: "사랑학과" },
  { id: "8", name: "갈등학과" },
  { id: "9", name: "이혼 치유학과" },
  { id: "10", name: "사별 회복학과" },
  { id: "11", name: "결혼학과" },
  { id: "12", name: "중년성학과" },
];

export const AdminCurriculumPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, token, user, loading: authLoading } = useAuth();
  
  const [contents, setContents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalContents, setTotalContents] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isGeneratingThumb, setIsGeneratingThumb] = useState(false);
  const contentTextareaRef = useRef(null);
  const contentImageInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    curriculum_id: "1",
    title: "",
    content: "",
    summary: "",
    thumbnail: "",
    media_type: "",
    media_url: "",
    youtube_id: "",
    tags: "",
    is_published: true,
  });

  useEffect(() => {
    if (authLoading) return; // wait for auth hydration
    if (!isAuthenticated) {
      navigate("/");
      return;
    }
    // Check if user is admin
    if (user?.email !== "admin@newcoupling.com") {
      alert("관리자만 접근 가능합니다.");
      navigate("/");
      return;
    }
    fetchContents();
  }, [isAuthenticated, user, navigate, authLoading]);

  const [isSeeding, setIsSeeding] = useState(false);

  // AI 자동 발행 상태
  const [autoBusy, setAutoBusy] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [autoRuns, setAutoRuns] = useState([]);
  const [autoRunsLoading, setAutoRunsLoading] = useState(false);
  const [autoCurriculumId, setAutoCurriculumId] = useState("");

  const fetchAutoRuns = async () => {
    setAutoRunsLoading(true);
    try {
      const res = await safeFetch(`${API_URL}/api/admin/curriculum/auto-runs?limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setAutoRuns(data.runs || []);
    } catch (e) {
      console.error(e);
    } finally {
      setAutoRunsLoading(false);
    }
  };

  useEffect(() => {
    if (token && user?.email === "admin@newcoupling.com") fetchAutoRuns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleAutoGenerateOne = async () => {
    const target = autoCurriculumId || "(가장 글이 적은 학과 자동 선택)";
    if (!window.confirm(`AI가 새 컨텐츠 1편을 생성·발행합니다.\n대상: ${target}\n약 30~60초 소요됩니다. 진행하시겠습니까?`)) return;
    setAutoBusy(true);
    try {
      const url = autoCurriculumId
        ? `${API_URL}/api/admin/curriculum/auto-generate?curriculum_id=${autoCurriculumId}`
        : `${API_URL}/api/admin/curriculum/auto-generate`;
      const res = await safeFetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "생성 실패");
      alert(`✅ 새 컨텐츠 발행 완료\n학과 #${data.content?.curriculum_id} · ${data.content?.title}\n본문 길이: ${data.content?.content_length}자`);
      await Promise.all([fetchContents(), fetchAutoRuns()]);
    } catch (e) {
      alert(`❌ 생성 실패: ${e.message}`);
    } finally {
      setAutoBusy(false);
    }
  };

  const handleRefreshThumbnails = async () => {
    if (!window.confirm("모든 컨텐츠의 썸네일을 학과별 큐레이션 이미지로 일괄 재정렬합니다.\n(사용자 업로드 자체 이미지는 보존)")) return;
    try {
      const res = await safeFetch(`${API_URL}/api/admin/curriculum/refresh-thumbnails`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      alert(`✅ 썸네일 재정렬 완료\n업데이트: ${data.updated || 0}개\n유지: ${data.skipped || 0}개`);
      await fetchContents();
    } catch (e) {
      alert("실패: " + e.message);
    }
  };

  const handleBulkUpgrade = async () => {
    // 먼저 dry run으로 대상 확인
    setBulkBusy(true);
    try {
      const dryRes = await safeFetch(`${API_URL}/api/admin/curriculum/regenerate-short?dry_run=true&min_length=1000&limit=20`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const dry = await dryRes.json();
      const count = dry.count || 0;
      if (count === 0) {
        alert("1000자 미만의 짧은 컨텐츠가 없습니다. 모두 적절한 길이입니다.");
        return;
      }
      const msg = `📝 ${count}개의 짧은 컨텐츠를 AI로 일괄 업그레이드합니다.\n\n` +
        `· 원본은 비공개 처리 (댓글·좋아요 보존)\n` +
        `· 동일 학과로 새 글 발행\n` +
        `· 약 ${count * 60}초 소요\n\n진행하시겠습니까?`;
      if (!window.confirm(msg)) return;
      const res = await safeFetch(`${API_URL}/api/admin/curriculum/regenerate-short?min_length=1000&limit=20`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      alert(`✅ 일괄 업그레이드 완료\n성공: ${data.upgraded_count || 0}개\n실패: ${data.failed_count || 0}개`);
      await Promise.all([fetchContents(), fetchAutoRuns()]);
    } catch (e) {
      alert(`❌ 실패: ${e.message}`);
    } finally {
      setBulkBusy(false);
    }
  };

  const handleSeedDefaults = async (force = false) => {
    const msg = force
      ? "🚀 각 학과에 무조건 1개씩 강제 추가합니다.\n\n기존 콘텐츠가 있어도 추가로 1개를 더 시드합니다.\n\n진행하시겠습니까?"
      : "각 학과별 기본 콘텐츠를 자동 추가합니다.\n(이미 콘텐츠가 있는 학과는 건너뜁니다)\n\n진행하시겠습니까?";
    if (!window.confirm(msg)) return;
    setIsSeeding(true);
    try {
      const url = `${API_URL}/api/admin/curriculum/seed-default${force ? "?force=true" : ""}`;
      const res = await safeFetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        alert(`✅ ${data.total_seeded}개 학과에 추가, ${data.total_skipped}개 건너뜀.`);
        fetchContents();
      } else {
        alert(`❌ ${data.detail || "시드 실패"}`);
      }
    } catch (e) {
      alert("네트워크 오류");
    } finally {
      setIsSeeding(false);
    }
  };

  const CURR_PAGE_SIZE = 30;
  const fetchContents = async () => {
    setIsLoading(true);
    try {
      // 이미 더보기로 불러온 개수를 유지하며 새로고침 (최소 한 페이지)
      const desired = Math.max(contents.length, CURR_PAGE_SIZE);
      const response = await safeFetch(`${API_URL}/api/admin/curriculum/contents?skip=0&limit=${desired}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setContents(data.contents);
        setTotalContents(data.total || data.contents.length);
      }
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMoreContents = async () => {
    setLoadingMore(true);
    try {
      const response = await safeFetch(`${API_URL}/api/admin/curriculum/contents?skip=${contents.length}&limit=${CURR_PAGE_SIZE}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setContents((prev) => [...prev, ...(data.contents || [])]);
        setTotalContents(data.total || 0);
      }
    } catch (err) {
      console.error("Load more error:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGenerateThumbnail = async () => {
    if (!formData.title?.trim()) {
      alert("제목을 먼저 입력해주세요.");
      return;
    }
    setIsGeneratingThumb(true);
    try {
      const deptName = curriculumOptions.find((c) => c.id === formData.curriculum_id)?.name || "";
      const res = await safeFetch(`${API_URL}/api/admin/curriculum/generate-image`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          title: formData.title,
          summary: formData.summary,
          content: formData.content,
          department: deptName,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "이미지 생성에 실패했습니다.");
      handleInputChange("thumbnail", data.url);
    } catch (e) {
      alert(`❌ ${e.message || "이미지 생성에 실패했습니다."}`);
    } finally {
      setIsGeneratingThumb(false);
    }
  };

  const handleContentImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("file_type", "image");
      const res = await safeFetch(`${API_URL}/api/upload`, { method: "POST", body: fd });
      if (res.ok) {
        const data = await res.json();
        const imgTag = `\n<img src="${data.url}" alt="${file.name}" style="max-width:100%;border-radius:8px;margin:12px 0;" />\n`;
        const ta = contentTextareaRef.current;
        if (ta) {
          const start = ta.selectionStart;
          const before = formData.content.substring(0, start);
          const after = formData.content.substring(ta.selectionEnd);
          handleInputChange("content", before + imgTag + after);
        } else {
          handleInputChange("content", formData.content + imgTag);
        }
      } else {
        alert("이미지 업로드에 실패했습니다.");
      }
    } catch {
      alert("이미지 업로드 중 오류가 발생했습니다.");
    } finally {
      setIsUploadingImage(false);
      if (contentImageInputRef.current) contentImageInputRef.current.value = "";
    }
  };

  const resetForm = () => {
    setFormData({
      curriculum_id: "1",
      title: "",
      content: "",
      summary: "",
      thumbnail: "",
      media_type: "",
      media_url: "",
      youtube_id: "",
      tags: "",
      is_published: true,
    });
    setEditingContent(null);
    setShowForm(false);
  };

  const handleEdit = (content) => {
    setFormData({
      curriculum_id: content.curriculum_id,
      title: content.title,
      content: content.content,
      summary: content.summary || "",
      thumbnail: content.thumbnail || "",
      media_type: content.media_type || "",
      media_url: content.media_url || "",
      youtube_id: content.youtube_id || "",
      tags: content.tags?.join(", ") || "",
      is_published: content.is_published,
    });
    setEditingContent(content);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const payload = {
      ...formData,
      tags: formData.tags.split(",").map(t => t.trim()).filter(t => t),
    };

    try {
      const url = editingContent
        ? `${API_URL}/api/curriculum/content/${editingContent.id}`
        : `${API_URL}/api/curriculum/content`;
      
      const response = await safeFetch(url, {
        method: editingContent ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        alert(editingContent ? "수정되었습니다." : "등록되었습니다.");
        resetForm();
        fetchContents();
      } else {
        const error = await response.json();
        alert(error.detail || "오류가 발생했습니다.");
      }
    } catch (err) {
      console.error("Submit error:", err);
      alert("오류가 발생했습니다.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (contentId) => {
    if (!window.confirm("정말 삭제하시겠습니까?")) return;

    try {
      const response = await safeFetch(`${API_URL}/api/curriculum/content/${contentId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        alert("삭제되었습니다.");
        fetchContents();
      }
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  const extractYoutubeId = (url) => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
    return match ? match[1] : "";
  };

  if (!isAuthenticated || user?.email !== "admin@newcoupling.com") {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9] pt-20">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-2 text-stone-500 hover:text-stone-700 mb-2"
            >
              <ArrowLeft className="w-4 h-4" />
              홈으로
            </button>
            <h1 className="text-2xl font-bold text-stone-800">연애사관학교 콘텐츠 관리</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => handleSeedDefaults(false)}
              disabled={isSeeding}
              variant="outline"
              className="flex items-center gap-2 border-burgundy-200 text-burgundy-700 hover:bg-burgundy-50"
              data-testid="seed-default-contents-btn"
              title="이미 콘텐츠가 있는 학과는 건너뜁니다"
            >
              {isSeeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              학과별 기본 콘텐츠 자동추가
            </Button>
            <Button
              onClick={() => handleSeedDefaults(true)}
              disabled={isSeeding}
              variant="outline"
              className="flex items-center gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="seed-default-contents-force-btn"
              title="이미 있어도 무조건 1개 추가"
            >
              {isSeeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              강제로 1개씩 추가
            </Button>
            <Button
              onClick={() => setShowForm(true)}
              className="btn-primary flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              새 콘텐츠 등록
            </Button>
          </div>
        </div>

        {/* AI 자동 발행 카드 */}
        <div className="bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-200 rounded-2xl p-5 mb-6" data-testid="ai-auto-publish-card">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center text-white">
                <Bot className="w-5 h-5" />
              </div>
              <div>
                <h2 className="font-bold text-stone-900 flex items-center gap-2">
                  AI 자동 발행
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-200 text-violet-800 font-semibold">GPT-4o</span>
                </h2>
                <p className="text-xs text-stone-600 mt-0.5 flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" />
                  매주 <strong>월·목요일 오전 9시(KST)</strong> 자동 발행 · 학과 라운드로빈
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* 1: 즉시 1편 생성 */}
            <div className="bg-white/70 rounded-xl p-4 border border-violet-100">
              <p className="text-[13px] font-semibold text-stone-800 mb-2 flex items-center gap-1.5">
                <Wand2 className="w-4 h-4 text-violet-600" />
                즉시 1편 자동 생성
              </p>
              <p className="text-[11px] text-stone-500 mb-3 leading-relaxed">
                AI가 새 주제로 1700~2200자 분량의 컨텐츠를 만들고 즉시 발행합니다.
              </p>
              <div className="flex gap-2">
                <select
                  value={autoCurriculumId}
                  onChange={(e) => setAutoCurriculumId(e.target.value)}
                  className="text-xs px-2 py-1.5 border border-stone-300 rounded-lg flex-1 min-w-0"
                  data-testid="auto-gen-curriculum-select"
                >
                  <option value="">자동 (글 적은 학과)</option>
                  {curriculumOptions.map((o) => (
                    <option key={o.id} value={o.id}>#{o.id} {o.name}</option>
                  ))}
                </select>
                <Button
                  onClick={handleAutoGenerateOne}
                  disabled={autoBusy}
                  className="bg-violet-600 hover:bg-violet-700 text-white text-xs px-3 py-1.5 h-auto"
                  data-testid="auto-gen-btn"
                >
                  {autoBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "발행"}
                </Button>
              </div>
            </div>

            {/* 2: 짧은 글 일괄 업그레이드 */}
            <div className="bg-white/70 rounded-xl p-4 border border-violet-100">
              <p className="text-[13px] font-semibold text-stone-800 mb-2 flex items-center gap-1.5">
                <Sparkles className="w-4 h-4 text-amber-600" />
                짧은 글 일괄 업그레이드
              </p>
              <p className="text-[11px] text-stone-500 mb-3 leading-relaxed">
                1000자 미만의 짧은 컨텐츠를 AI로 1700자 이상으로 재작성합니다.
              </p>
              <Button
                onClick={handleBulkUpgrade}
                disabled={bulkBusy}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white text-xs px-3 py-1.5 h-auto"
                data-testid="bulk-upgrade-btn"
              >
                {bulkBusy ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5 inline" /> 처리 중…</> : "대상 확인 후 일괄 처리"}
              </Button>
            </div>
          </div>

          {/* 썸네일 일괄 재정렬 */}
          <div className="mt-3 bg-white/70 rounded-xl p-3 border border-violet-100 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <ImagePlus className="w-4 h-4 text-emerald-600" />
              <div>
                <p className="text-[12px] font-semibold text-stone-800">썸네일 일괄 재정렬</p>
                <p className="text-[10.5px] text-stone-500">모든 컨텐츠를 학과별 큐레이션 이미지로 동기화 (사용자 업로드 이미지는 보존)</p>
              </div>
            </div>
            <Button
              onClick={handleRefreshThumbnails}
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-3 py-1.5 h-auto"
              data-testid="refresh-thumbnails-btn"
            >
              실행
            </Button>
          </div>

          {/* 발행 이력 */}
          <div className="mt-4 bg-white/60 rounded-xl p-3 border border-violet-100">
            <p className="text-[12px] font-semibold text-stone-700 mb-2">최근 자동 발행 이력 (최대 5개)</p>
            {autoRunsLoading ? (
              <p className="text-[11px] text-stone-400">불러오는 중…</p>
            ) : autoRuns.length === 0 ? (
              <p className="text-[11px] text-stone-400">아직 자동 발행 이력이 없습니다.</p>
            ) : (
              <ul className="space-y-1 text-[11px]">
                {autoRuns.slice(0, 5).map((r, i) => (
                  <li key={i} className="flex items-center gap-2 text-stone-600 font-mono">
                    <span className={`shrink-0 px-1.5 py-0.5 rounded ${r.status === "success" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                      {r.status === "success" ? "✓" : "✗"}
                    </span>
                    <span className="shrink-0 text-stone-400 w-32 truncate">
                      {r.run_at ? new Date(r.run_at).toLocaleString("ko-KR", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}
                    </span>
                    <span className="shrink-0">{r.trigger === "scheduled" ? "🤖 자동" : r.trigger === "bulk_upgrade" ? "📝 일괄" : "👤 수동"}</span>
                    <span className="shrink-0">학과 #{r.curriculum_id}</span>
                    <span className="shrink-0 text-stone-500">{r.content_length ? `${r.content_length}자` : (r.error?.slice(0, 50) || "—")}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Content Form Modal */}
        {showForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-stone-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-lg font-bold text-stone-800">
                  {editingContent ? "콘텐츠 수정" : "새 콘텐츠 등록"}
                </h2>
                <button onClick={resetForm} className="p-2 hover:bg-stone-100 rounded-full">
                  <X className="w-5 h-5 text-stone-600" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {/* Curriculum Selection */}
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">학과 선택</label>
                  <select
                    value={formData.curriculum_id}
                    onChange={(e) => handleInputChange("curriculum_id", e.target.value)}
                    className="w-full px-4 py-2.5 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20"
                  >
                    {curriculumOptions.map((opt) => (
                      <option key={opt.id} value={opt.id}>{opt.name}</option>
                    ))}
                  </select>
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">제목 *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange("title", e.target.value)}
                    className="w-full px-4 py-2.5 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20"
                    placeholder="콘텐츠 제목"
                    required
                  />
                </div>

                {/* Summary */}
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">요약</label>
                  <input
                    type="text"
                    value={formData.summary}
                    onChange={(e) => handleInputChange("summary", e.target.value)}
                    className="w-full px-4 py-2.5 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20"
                    placeholder="간단한 요약"
                  />
                </div>

                {/* Media Type */}
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">미디어 타입</label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => handleInputChange("media_type", "")}
                      className={`px-4 py-2 rounded-lg border transition-colors ${
                        !formData.media_type ? "bg-burgundy-50 border-burgundy-300 text-burgundy-600" : "border-stone-200"
                      }`}
                    >
                      없음
                    </button>
                    <button
                      type="button"
                      onClick={() => handleInputChange("media_type", "image")}
                      className={`px-4 py-2 rounded-lg border transition-colors flex items-center gap-2 ${
                        formData.media_type === "image" ? "bg-burgundy-50 border-burgundy-300 text-burgundy-600" : "border-stone-200"
                      }`}
                    >
                      <Image className="w-4 h-4" /> 이미지
                    </button>
                    <button
                      type="button"
                      onClick={() => handleInputChange("media_type", "video")}
                      className={`px-4 py-2 rounded-lg border transition-colors flex items-center gap-2 ${
                        formData.media_type === "video" ? "bg-burgundy-50 border-burgundy-300 text-burgundy-600" : "border-stone-200"
                      }`}
                    >
                      <Video className="w-4 h-4" /> 비디오
                    </button>
                    <button
                      type="button"
                      onClick={() => handleInputChange("media_type", "youtube")}
                      className={`px-4 py-2 rounded-lg border transition-colors flex items-center gap-2 ${
                        formData.media_type === "youtube" ? "bg-burgundy-50 border-burgundy-300 text-burgundy-600" : "border-stone-200"
                      }`}
                    >
                      <Youtube className="w-4 h-4" /> 유튜브
                    </button>
                  </div>
                </div>

                {/* Media URL */}
                {formData.media_type && formData.media_type !== "youtube" && (
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">미디어 URL</label>
                    <input
                      type="url"
                      value={formData.media_url}
                      onChange={(e) => handleInputChange("media_url", e.target.value)}
                      className="w-full px-4 py-2.5 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20"
                      placeholder="이미지 또는 비디오 URL"
                    />
                  </div>
                )}

                {/* YouTube */}
                {formData.media_type === "youtube" && (
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">유튜브 URL 또는 ID</label>
                    <input
                      type="text"
                      value={formData.youtube_id}
                      onChange={(e) => {
                        const value = e.target.value;
                        const id = extractYoutubeId(value) || value;
                        handleInputChange("youtube_id", id);
                      }}
                      className="w-full px-4 py-2.5 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20"
                      placeholder="https://www.youtube.com/watch?v=... 또는 VIDEO_ID"
                    />
                    {formData.youtube_id && (
                      <div className="mt-2 aspect-video bg-black rounded-lg overflow-hidden">
                        <iframe
                          src={`https://www.youtube.com/embed/${formData.youtube_id}`}
                          className="w-full h-full"
                          frameBorder="0"
                          allowFullScreen
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Thumbnail — AI 자동 생성 */}
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">썸네일 이미지 (AI 자동 생성)</label>
                  <p className="text-xs text-stone-400 mb-2">제목·요약·본문 내용을 기반으로 AI가 어울리는 이미지를 만들어줍니다.</p>
                  {formData.thumbnail ? (
                    <div className="relative w-full rounded-xl overflow-hidden border border-stone-200 bg-stone-50">
                      <img
                        src={formData.thumbnail.startsWith("http") ? formData.thumbnail : `${API_URL}${formData.thumbnail}`}
                        alt="썸네일 미리보기"
                        className="w-full max-h-56 object-cover"
                        data-testid="curriculum-thumb-preview"
                      />
                      <div className="flex gap-2 p-2 bg-white">
                        <button
                          type="button"
                          onClick={handleGenerateThumbnail}
                          disabled={isGeneratingThumb}
                          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium bg-burgundy-50 hover:bg-burgundy-100 text-burgundy-700 rounded-lg transition-colors disabled:opacity-50"
                          data-testid="curriculum-thumb-regenerate"
                        >
                          {isGeneratingThumb ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                          {isGeneratingThumb ? "생성 중..." : "다시 생성"}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleInputChange("thumbnail", "")}
                          disabled={isGeneratingThumb}
                          className="px-3 py-2 text-xs font-medium bg-stone-100 hover:bg-stone-200 text-stone-600 rounded-lg transition-colors disabled:opacity-50"
                          data-testid="curriculum-thumb-remove"
                        >
                          삭제
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={handleGenerateThumbnail}
                      disabled={isGeneratingThumb}
                      className="w-full flex items-center justify-center gap-2 px-4 py-4 border-2 border-dashed border-burgundy-200 hover:border-burgundy-300 hover:bg-burgundy-50/50 rounded-xl text-burgundy-600 font-medium transition-colors disabled:opacity-50"
                      data-testid="curriculum-thumb-generate"
                    >
                      {isGeneratingThumb ? <Loader2 className="w-5 h-5 animate-spin" /> : <Wand2 className="w-5 h-5" />}
                      {isGeneratingThumb ? "AI가 이미지를 생성하는 중..." : "AI로 썸네일 이미지 생성"}
                    </button>
                  )}
                </div>

                {/* Content */}
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">본문 내용 *</label>
                  <div className="flex items-center gap-2 mb-2">
                    <button
                      type="button"
                      onClick={() => contentImageInputRef.current?.click()}
                      disabled={isUploadingImage}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-stone-100 hover:bg-stone-200 rounded-lg text-stone-600 transition-colors disabled:opacity-50"
                    >
                      {isUploadingImage ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
                      {isUploadingImage ? "업로드중..." : "본문에 이미지 삽입"}
                    </button>
                    <input
                      ref={contentImageInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleContentImageUpload}
                      className="hidden"
                    />
                    <span className="text-xs text-stone-400">커서 위치에 이미지가 삽입됩니다</span>
                  </div>
                  <textarea
                    ref={contentTextareaRef}
                    value={formData.content}
                    onChange={(e) => handleInputChange("content", e.target.value)}
                    className="w-full px-4 py-3 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 min-h-[300px] font-mono text-sm"
                    placeholder="본문 내용을 입력하세요. 줄바꿈이 그대로 반영됩니다."
                    required
                  />
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">태그 (쉼표로 구분)</label>
                  <input
                    type="text"
                    value={formData.tags}
                    onChange={(e) => handleInputChange("tags", e.target.value)}
                    className="w-full px-4 py-2.5 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20"
                    placeholder="자존감, 자기사랑, 힐링"
                  />
                </div>

                {/* Published */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="is_published"
                    checked={formData.is_published}
                    onChange={(e) => handleInputChange("is_published", e.target.checked)}
                    className="w-4 h-4 text-burgundy-500 rounded"
                  />
                  <label htmlFor="is_published" className="text-sm text-stone-700">
                    즉시 공개
                  </label>
                </div>

                {/* Buttons */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={resetForm}>
                    취소
                  </Button>
                  <Button type="submit" className="btn-primary flex items-center gap-2" disabled={isSubmitting}>
                    {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {editingContent ? "수정" : "등록"}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Content List */}
        {isLoading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-burgundy-500" />
          </div>
        ) : contents.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-stone-200">
            <p className="text-stone-500">등록된 콘텐츠가 없습니다.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">학과</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">제목</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">미디어</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-stone-600">조회</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-stone-600">상태</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-stone-600">관리</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-200">
                {contents.map((content) => (
                  <tr key={content.id} className="hover:bg-stone-50">
                    <td className="px-4 py-3 text-sm text-stone-600">
                      {curriculumOptions.find(o => o.id === content.curriculum_id)?.name || content.curriculum_id}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-stone-800 line-clamp-1">{content.title}</p>
                      <p className="text-xs text-stone-500">{new Date(content.created_at).toLocaleDateString("ko-KR")}</p>
                    </td>
                    <td className="px-4 py-3">
                      {content.media_type === "youtube" && <Youtube className="w-5 h-5 text-red-500" />}
                      {content.media_type === "video" && <Video className="w-5 h-5 text-blue-500" />}
                      {content.media_type === "image" && <Image className="w-5 h-5 text-green-500" />}
                      {!content.media_type && <span className="text-stone-400">-</span>}
                    </td>
                    <td className="px-4 py-3 text-center text-sm text-stone-600">{content.views}</td>
                    <td className="px-4 py-3 text-center">
                      {content.is_published ? (
                        <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                          <Eye className="w-3 h-3" /> 공개
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-stone-500 bg-stone-100 px-2 py-1 rounded-full">
                          <EyeOff className="w-3 h-3" /> 비공개
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleEdit(content)}
                          className="p-2 hover:bg-stone-100 rounded-lg text-stone-600"
                          title="수정"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(content.id)}
                          className="p-2 hover:bg-red-50 rounded-lg text-red-500"
                          title="삭제"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {contents.length < totalContents && (
              <div className="flex flex-col items-center gap-2 py-6 border-t border-stone-100">
                <p className="text-xs text-stone-400" data-testid="curriculum-count">
                  {contents.length} / {totalContents}개 표시 중
                </p>
                <button
                  onClick={loadMoreContents}
                  disabled={loadingMore}
                  className="px-6 py-2.5 rounded-xl bg-stone-800 hover:bg-stone-900 text-white text-sm font-bold transition-colors disabled:opacity-50"
                  data-testid="curriculum-load-more-btn"
                >
                  {loadingMore ? "불러오는 중..." : "페이지 더보기"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCurriculumPage;
